<template>
  <div class="unauth">
    <!-- Ambient background orbs -->
    <div class="unauth__orb unauth__orb--a" />
    <div class="unauth__orb unauth__orb--b" />

    <div class="unauth__card">
      <!-- Icon -->
      <div class="unauth__icon-wrap">
        <div class="unauth__icon-ring unauth__icon-ring--outer" />
        <div class="unauth__icon-ring unauth__icon-ring--inner" />
        <div class="unauth__icon-shell">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="unauth__icon-svg">
            <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3A3 3 0 0 0 3.75 12v6.75A3.75 3.75 0 0 0 7.5 22.5h9a3.75 3.75 0 0 0 3.75-3.75V12a3 3 0 0 0-3-3V6.75A5.25 5.25 0 0 0 12 1.5Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Zm-6 6.75a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0Z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>

      <!-- Error code badge -->
      <div class="unauth__badge">403</div>

      <!-- Heading -->
      <h1 class="unauth__title">Access Restricted</h1>
      <p class="unauth__subtitle">
        You don't have the required permissions to view this page.
        Contact your administrator if you believe this is a mistake.
      </p>

      <!-- Permission hint (if available) -->
      <div v-if="requiredPermission" class="unauth__hint">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="unauth__hint-icon">
          <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clip-rule="evenodd" />
        </svg>
        <span>
          Requires permission: <code class="unauth__perm-code">{{ requiredPermission }}</code>
        </span>
      </div>

      <!-- Action buttons -->
      <div class="unauth__actions">
        <button class="unauth__btn unauth__btn--primary" @click="goToDashboard">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.293 2.293a1 1 0 0 1 1.414 0l7 7A1 1 0 0 1 17 11h-1v6a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-3a1 1 0 0 1-1-1H9a1 1 0 0 1-1 1v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6H3a1 1 0 0 1-.707-1.707l7-7Z" clip-rule="evenodd" />
          </svg>
          Go to Dashboard
        </button>
        <button class="unauth__btn unauth__btn--secondary" @click="goBack">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clip-rule="evenodd" />
          </svg>
          Go Back
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Unauthorized',
  computed: {
    requiredPermission () {
      // The permission middleware passes it via query or the previous route's meta
      return this.$route.query.permission || null
    }
  },
  methods: {
    goToDashboard () {
      this.$router.push({ name: 'dashboard' })
    },
    goBack () {
      // If there's real history, go back; otherwise go to dashboard
      if (window.history.length > 2) {
        this.$router.go(-1)
      } else {
        this.$router.push({ name: 'dashboard' })
      }
    }
  }
}
</script>

<style scoped>
/* ─── ROOT ─────────────────────────────────────────────── */
.unauth {
  min-height: calc(100vh - 64px); /* account for the app-bar */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  position: relative;
  overflow: hidden;
  background: transparent;
}

/* Ambient orbs (match login brand panel style) */
.unauth__orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(90px);
  opacity: 0.35;
}
.unauth__orb--a {
  width: 480px; height: 480px;
  top: -120px; right: -100px;
  background: radial-gradient(circle, #a78bfa 0%, transparent 70%);
  animation: orb-drift 12s ease-in-out infinite;
}
.unauth__orb--b {
  width: 380px; height: 380px;
  bottom: -80px; left: -60px;
  background: radial-gradient(circle, #60a5fa 0%, transparent 70%);
  animation: orb-drift 15s ease-in-out infinite reverse;
  animation-delay: -5s;
}
@keyframes orb-drift {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(24px, -20px); }
}

/* ─── CARD ──────────────────────────────────────────────── */
.unauth__card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 480px;
  width: 100%;
  background: #ffffff;
  border-radius: 28px;
  border: 1px solid #e2e8f0;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.07),
    0 20px 60px -8px rgba(99, 102, 241, 0.14);
  padding: 52px 44px 48px;
}

/* Dark mode */
.theme--dark .unauth__card {
  background: #1e1e2e;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.4),
    0 20px 60px -8px rgba(99, 102, 241, 0.25);
}

/* ─── ICON ──────────────────────────────────────────────── */
.unauth__icon-wrap {
  position: relative;
  width: 96px;
  height: 96px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.unauth__icon-ring {
  position: absolute;
  border-radius: 50%;
  border: 1.5px solid rgba(99, 102, 241, 0.2);
  animation: ring-pulse 2.8s ease-out infinite;
}
.unauth__icon-ring--outer {
  width: 96px; height: 96px;
  animation-delay: 0s;
}
.unauth__icon-ring--inner {
  width: 72px; height: 72px;
  animation-delay: 0.4s;
  border-color: rgba(99, 102, 241, 0.35);
}
@keyframes ring-pulse {
  0%   { transform: scale(1);    opacity: 1; }
  80%  { transform: scale(1.25); opacity: 0; }
  100% { transform: scale(1.25); opacity: 0; }
}
.unauth__icon-shell {
  width: 72px; height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ede9fe 0%, #dbeafe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.2);
}
.theme--dark .unauth__icon-shell {
  background: linear-gradient(135deg, rgba(109, 40, 217, 0.3) 0%, rgba(37, 99, 235, 0.3) 100%);
}
.unauth__icon-svg {
  width: 32px; height: 32px;
  color: #4f46e5;
}
.theme--dark .unauth__icon-svg {
  color: #818cf8;
}

/* ─── BADGE ─────────────────────────────────────────────── */
.unauth__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  margin-bottom: 16px;
}
.theme--dark .unauth__badge {
  background: rgba(220, 38, 38, 0.12);
  color: #f87171;
  border-color: rgba(220, 38, 38, 0.25);
}

/* ─── TEXT ──────────────────────────────────────────────── */
.unauth__title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.025em;
  margin: 0 0 10px;
  line-height: 1.2;
}
.theme--dark .unauth__title { color: #f1f5f9; }

.unauth__subtitle {
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.65;
  margin: 0 0 24px;
  max-width: 340px;
}
.theme--dark .unauth__subtitle { color: #94a3b8; }

/* ─── PERMISSION HINT ───────────────────────────────────── */
.unauth__hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  font-size: 0.825rem;
  color: #1e40af;
  text-align: left;
  margin-bottom: 28px;
  width: 100%;
  box-sizing: border-box;
}
.theme--dark .unauth__hint {
  background: rgba(37, 99, 235, 0.1);
  border-color: rgba(37, 99, 235, 0.25);
  color: #93c5fd;
}
.unauth__hint-icon {
  flex-shrink: 0;
  width: 16px; height: 16px;
  margin-top: 1px;
}
.unauth__perm-code {
  display: inline-block;
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 5px;
  padding: 1px 7px;
  font-size: 0.8rem;
  font-family: 'Courier New', monospace;
  font-weight: 600;
}
.theme--dark .unauth__perm-code {
  background: rgba(29, 78, 216, 0.2);
  color: #93c5fd;
}

/* ─── ACTIONS ───────────────────────────────────────────── */
.unauth__actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.unauth__btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 20px;
  font-size: 0.875rem;
  font-weight: 700;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s, opacity 0.18s, background 0.18s;
  letter-spacing: 0.01em;
}
.unauth__btn svg {
  width: 17px; height: 17px;
  flex-shrink: 0;
}
.unauth__btn:hover { transform: translateY(-2px); }
.unauth__btn:active { transform: translateY(0); }

.unauth__btn--primary {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #3730a3 100%);
  color: #ffffff;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.38);
}
.unauth__btn--primary:hover {
  box-shadow: 0 8px 28px rgba(99, 102, 241, 0.48);
}

.unauth__btn--secondary {
  background: #f8fafc;
  color: #334155;
  border: 1.5px solid #e2e8f0;
  box-shadow: none;
}
.unauth__btn--secondary:hover {
  background: #f1f5f9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}
.theme--dark .unauth__btn--secondary {
  background: rgba(255, 255, 255, 0.05);
  color: #cbd5e1;
  border-color: rgba(255, 255, 255, 0.1);
}
.theme--dark .unauth__btn--secondary:hover {
  background: rgba(255, 255, 255, 0.09);
}

/* ─── RESPONSIVE ────────────────────────────────────────── */
@media (max-width: 600px) {
  .unauth__card {
    padding: 40px 24px 36px;
    border-radius: 20px;
  }
  .unauth__title { font-size: 1.45rem; }
}
</style>
